import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { storeAuth, clearStoredRole, nactiTechnikyProLogin, verifyPin, getTokenPayload, jeDispecerTechnik } from '../auth';
import { formatCzechDateTime, pole } from '../utils';
import { STAV_TIKET_LABEL, STAV_TIKET_COLOR, roleStitek } from '../tiketyConst';
import { selectStyle } from '../ui';
import { najdiSvatek } from '../svatky';
import { useNovaPrirazeni, odznakStyl } from '../novaPrirazeni';

const API = process.env.REACT_APP_API_URL || '/api';

function getPozdrav() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Dobré ráno! 🌅';
  if (h >= 12 && h < 18) return 'Dobré odpoledne! ☀️';
  if (h >= 18 && h < 22) return 'Dobrý večer! 🌆';
  return 'Dobrou noc! 🌙';
}

const DNY_V_TYDNU = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
const MESICE_GENITIV = [
  'ledna', 'února', 'března', 'dubna', 'května', 'června',
  'července', 'srpna', 'září', 'října', 'listopadu', 'prosince',
];

// "Středa, 23. září 2026 | 10:16:42"
function formatDatumCas(d) {
  const pad = n => String(n).padStart(2, '0');
  const den = DNY_V_TYDNU[d.getDay()];
  const mesic = MESICE_GENITIV[d.getMonth()];
  const cas = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return `${den}, ${d.getDate()}. ${mesic} ${d.getFullYear()} | ${cas}`;
}

function useHodiny() {
  const [ted, setTed] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTed(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return ted;
}

// ── Rozšířené uvítání (datum/čas živě, kdo je přihlášen, dnešní svátek) ────────

function UvitaciInfo() {
  const ted = useHodiny();
  const payload = getTokenPayload();
  const svatek = najdiSvatek(ted);

  const jmeno = payload?.jmeno ? String(payload.jmeno).split(' ')[0] : null;
  const roleLabel = roleStitek(payload);

  return (
    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
      <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '4px' }}>
        {formatDatumCas(ted)}
      </div>
      {jmeno && roleLabel && (
        <div style={{ color: '#374151', fontSize: '14px', marginBottom: '4px' }}>
          Přihlášen: <strong>{jmeno}</strong> ({roleLabel})
        </div>
      )}
      {svatek && (
        <div style={{ color: '#3ab54a', fontSize: '14px' }}>
          🎉 Dnes má svátek: <strong>{svatek}</strong>
        </div>
      )}
    </div>
  );
}

const btnPrimary = {
  display: 'block', width: '100%', maxWidth: '360px', margin: '0 auto 14px',
  padding: '18px', fontSize: '17px', borderRadius: '8px', border: 'none',
  cursor: 'pointer', fontWeight: 'bold', background: '#3ab54a', color: 'white',
};

const btnOutline = {
  display: 'block', width: '100%', maxWidth: '360px', margin: '0 auto 14px',
  padding: '18px', fontSize: '17px', borderRadius: '8px', border: '2px solid #9ca3af',
  cursor: 'pointer', fontWeight: 'bold', background: 'transparent', color: '#555555',
};

// ── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color, small }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '8px',
      padding: small ? '10px 6px' : '14px 10px',
      border: `1px solid ${color}30`, textAlign: 'center',
    }}>
      <div style={{ fontSize: small ? '22px' : '30px', fontWeight: 'bold', color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', lineHeight: 1.3 }}>{label}</div>
    </div>
  );
}

// ── Admin dashboard ───────────────────────────────────────────────────────────

function raw(v) { return v ? String(v).split('.').pop() : ''; }

function AdminDashboard({ naviguj }) {
  const [dash, setDash] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tikety`).then(res => {
      const all = pole(res.data);
      const now = new Date();
      const yyyymm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const mesic = all.filter(t => String(t.prijato_at).startsWith(yyyymm));

      const stavy = {};
      const resitelMap = {};
      mesic.forEach(t => {
        const stav = raw(t.stav);
        stavy[stav] = (stavy[stav] || 0) + 1;
        if (t.resitel) resitelMap[t.resitel.jmeno] = (resitelMap[t.resitel.jmeno] || 0) + 1;
      });

      setDash({
        celkem:      mesic.length,
        stavy,
        topResitele: Object.entries(resitelMap).sort((a, b) => b[1] - a[1]).slice(0, 3),
        posledni:    all.slice(0, 5),
        mesicLabel:  now.toLocaleString('cs-CZ', { month: 'long', year: 'numeric' }),
      });
    }).catch(() => {});
  }, []);

  if (!dash) return null;

  return (
    <div style={{ marginTop: '20px', textAlign: 'left' }}>
      <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>
        Statistiky — {dash.mesicLabel}
      </div>

      {/* Celkem */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '8px', marginBottom: '8px' }}>
        <StatCard label="Tiketů přijato" value={dash.celkem} color="#3ab54a" />
      </div>

      {/* Stavy */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '16px' }}>
        {Object.entries(STAV_TIKET_LABEL).map(([k, label]) => (
          <StatCard key={k} label={label} value={dash.stavy[k] || 0} color={STAV_TIKET_COLOR[k] || '#9ca3af'} small />
        ))}
      </div>

      {/* Top 3 řešitelé */}
      {dash.topResitele.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>
            Nejaktivnější řešitelé
          </div>
          {dash.topResitele.map(([jmeno, pocet], i) => (
            <div key={jmeno} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: '#fff', borderRadius: '6px', marginBottom: '4px', fontSize: '13px', border: '1px solid #f0f0f0' }}>
              <span style={{ color: '#555' }}><span style={{ color: '#9ca3af', marginRight: '6px' }}>{i + 1}.</span>{jmeno}</span>
              <span style={{ color: '#3ab54a', fontWeight: 'bold' }}>{pocet} tiketů</span>
            </div>
          ))}
        </div>
      )}

      {/* Posledních 5 */}
      <div>
        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>
          Posledních 5 tiketů
        </div>
        {dash.posledni.map(t => (
          <div key={t.id}
            onClick={() => naviguj('tiket-detail', t.id)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#fff', borderRadius: '6px', marginBottom: '4px', fontSize: '13px', cursor: 'pointer', border: '1px solid #f0f0f0' }}
          >
            <div>
              <span style={{ fontWeight: 'bold', color: '#3ab54a' }}>{t.obj}</span>
              <span style={{ color: '#6b7280', marginLeft: '8px' }}>{t.zakaznik_nazev}</span>
              <span style={{ color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>{t.resitel?.jmeno || '—'}</span>
            </div>
            <div style={{ color: '#9ca3af', fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>{formatCzechDateTime(t.prijato_at)}</div>
          </div>
        ))}
        {dash.posledni.length === 0 && (
          <p style={{ color: '#9ca3af', fontSize: '13px' }}>Žádné tikety.</p>
        )}
      </div>
    </div>
  );
}

// ── Volba role ────────────────────────────────────────────────────────────────

function VolbaRole({ onVybrat }) {
  const [mode, setMode] = useState('none'); // 'none' | 'technik' | 'admin'
  const [technici, setTechnici] = useState([]);
  const [technikId, setTechnikId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [chyba, setChyba] = useState('');

  const otevritTechnik = async () => {
    setMode('technik'); setPin(''); setChyba('');
    try {
      const data = await nactiTechnikyProLogin();
      setTechnici(data);
      if (data.length > 0) setTechnikId(String(data[0].id));
    } catch {
      setChyba('Nepodařilo se načíst seznam uživatelů.');
    }
  };

  const prihlasit = async () => {
    if (!pin) return;
    setLoading(true); setChyba('');
    try {
      const result = await verifyPin(mode, pin, mode === 'technik' ? Number(technikId) : null);
      storeAuth(result.token, result.role, result.jmeno);
      onVybrat(result.role);
    } catch (e) {
      setChyba(e.response?.data?.detail || e.message || 'Neplatný PIN');
      setPin('');
    }
    setLoading(false);
  };

  const pinInput = (
    <input
      type="password" inputMode="numeric" value={pin} autoFocus
      onChange={e => { setPin(e.target.value); setChyba(''); }}
      onKeyDown={e => e.key === 'Enter' && prihlasit()}
      style={{
        display: 'block', width: '100%', padding: '14px', fontSize: '20px',
        textAlign: 'center', borderRadius: '8px', letterSpacing: '6px',
        border: chyba ? '2px solid #ef4444' : '2px solid #9ca3af',
        marginBottom: '8px', boxSizing: 'border-box',
      }}
    />
  );

  return (
    <div style={{ textAlign: 'center', paddingTop: '48px' }}>
      <img src="/logo.png" alt="ENTSOL" style={{ height: '80px', marginBottom: '20px' }} />
      <h1 style={{ color: '#333333', marginBottom: '8px', fontSize: '24px', fontWeight: 'bold' }}>
        Servisní zásahy
      </h1>
      <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '36px' }}>Kdo jste?</p>

      {mode === 'none' && (
        <>
          <button style={btnPrimary} onClick={otevritTechnik}>Přihlásit se</button>
          <button style={btnOutline} onClick={() => { setMode('admin'); setPin(''); setChyba(''); }}>Nouzový přístup (admin PIN)</button>
        </>
      )}

      {mode === 'technik' && (
        <div style={{ maxWidth: '360px', margin: '0 auto' }}>
          <div style={{ marginBottom: '12px', textAlign: 'left' }}>
            <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Vyberte uživatele:</label>
            <select value={technikId} onChange={e => setTechnikId(e.target.value)}
              style={{ ...selectStyle, fontSize: '15px', border: '2px solid #9ca3af', marginBottom: '12px' }}>
              {technici.map(t => <option key={t.id} value={t.id}>{t.jmeno}</option>)}
            </select>
            <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>PIN:</label>
            {pinInput}
          </div>
          {chyba && <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '8px' }}>{chyba}</p>}
          <button style={{ ...btnPrimary, marginBottom: '10px' }} onClick={prihlasit} disabled={loading}>
            {loading ? 'Ověřuji...' : 'Přihlásit se'}
          </button>
          <button style={{ ...btnOutline, padding: '12px', fontSize: '15px' }} onClick={() => { setMode('none'); setPin(''); setChyba(''); }}>Zpět</button>
        </div>
      )}

      {mode === 'admin' && (
        <div style={{ maxWidth: '360px', margin: '0 auto' }}>
          <p style={{ color: '#333333', fontSize: '15px', marginBottom: '12px' }}>Admin PIN:</p>
          {pinInput}
          {chyba && <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '8px' }}>{chyba}</p>}
          <button style={{ ...btnPrimary, marginBottom: '10px' }} onClick={prihlasit} disabled={loading}>
            {loading ? 'Ověřuji...' : 'Potvrdit'}
          </button>
          <button style={{ ...btnOutline, padding: '12px', fontSize: '15px' }} onClick={() => { setMode('none'); setPin(''); setChyba(''); }}>Zpět</button>
        </div>
      )}
    </div>
  );
}

// ── Rozcestí ─────────────────────────────────────────────────────────────────

function Rozcesti({ role, setRole, setStranka, naviguj }) {
  const dispecerTechnik = jeDispecerTechnik();
  const [nove] = useNovaPrirazeni(role === 'technik' || dispecerTechnik);
  const handleVybrat = (novaRole) => { setRole(novaRole); };
  const handleOdhlasit = () => { clearStoredRole(); setRole(null); };

  if (!role) return <VolbaRole onVybrat={handleVybrat} />;

  return (
    <div style={{ textAlign: 'center', paddingTop: '48px' }}>
      <img src="/logo.png" alt="ENTSOL" style={{ height: '80px', marginBottom: '20px' }} />
      <h1 style={{ color: '#333333', marginBottom: '12px', fontSize: '24px', fontWeight: 'bold' }}>
        Servisní zásahy
      </h1>
      <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '16px' }}>
        {getPozdrav()}
      </p>

      <UvitaciInfo />

      {(role === 'dispecer' || role === 'admin') && (
        <button style={btnPrimary} onClick={() => setStranka('novy-tiket')}>Nový tiket</button>
      )}
      {role === 'technik'
        ? <button style={btnPrimary} onClick={() => setStranka('moje-tikety')}>
            Moje tikety a fronta
            {nove > 0 && <span style={odznakStyl} title="Nově přiřazené tikety, které jste ještě neotevřeli">{nove}</span>}
          </button>
        : <button style={btnPrimary} onClick={() => setStranka('aktivni-tikety')}>Přehled tiketů</button>}
      {dispecerTechnik && (
        <button style={btnPrimary} onClick={() => setStranka('moje-tikety')}>
          Moje tikety a fronta
          {nove > 0 && <span style={odznakStyl} title="Nově přiřazené tikety, které jste ještě neotevřeli">{nove}</span>}
        </button>
      )}
      {role === 'admin' && (
        <button style={btnOutline} onClick={() => naviguj('admin', null, 'konfigurace')}>Konfigurace</button>
      )}

      {(role === 'dispecer' || role === 'admin') && (
        <button style={btnOutline} onClick={() => setStranka('co-je-noveho')}>Co je nového</button>
      )}

      <button onClick={handleOdhlasit} style={{
        background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px',
        cursor: 'pointer', marginTop: '8px', textDecoration: 'underline',
      }}>
        Odhlásit se / změnit roli
      </button>

      {role === 'admin' && <AdminDashboard naviguj={naviguj} />}
    </div>
  );
}

export default Rozcesti;
